// ─── Onboarding Context ─────────────────────────────────────────
// Derives onboarding state from existing data (projects, subscriber, runs).
// No new API endpoint needed — all state is computed client-side.
//
// Tiers:
//   new_user       → 0 projects, no credentials
//   has_project    → ≥1 project created (wizard started or complete)
//   has_first_sync → ≥1 project with wizard_complete=true
//   has_first_run  → ≥1 completed pipeline run
//   graduated      → ≥1 PR merged (or 3+ completed runs)
//
// The Welcome page renders when tier === 'new_user'.
// The sidebar uses tier for progressive disclosure.
import React, { createContext, useContext, useMemo, useCallback, useState } from 'react';

export type OnboardingTier = 'new_user' | 'has_project' | 'has_first_sync' | 'has_first_run' | 'graduated';

export interface OnboardingState {
  tier: OnboardingTier;
  /** True when the user has dismissed the welcome screen manually */
  welcomeDismissed: boolean;
  /** True when the first-win celebration has been shown and dismissed */
  celebrationShown: boolean;
  /** Dismiss the welcome screen */
  dismissWelcome: () => void;
  /** Mark celebration as shown */
  markCelebrationShown: () => void;
  /** Whether to show the welcome page (tier=new_user and not dismissed) */
  showWelcome: boolean;
}

const OnboardingContext = createContext<OnboardingState>({
  tier: 'new_user',
  welcomeDismissed: false,
  celebrationShown: false,
  dismissWelcome: () => {},
  markCelebrationShown: () => {},
  showWelcome: true,
});

export function useOnboarding() {
  return useContext(OnboardingContext);
}

interface Project {
  project_id: string;
  wizard_complete?: boolean;
  source?: string;
}

interface Run {
  id?: string;
  status?: string;
  pr_number?: number;
}

interface OnboardingProviderProps {
  children: React.ReactNode;
  projects: Project[];
  runs?: Run[];
}

export function OnboardingProvider({ children, projects, runs = [] }: OnboardingProviderProps) {
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [celebrationShown, setCelebrationShown] = useState(false);

  const tier = useMemo<OnboardingTier>(() => {
    if (projects.length === 0) return 'new_user';

    const hasCompletedWizard = projects.some(p => p.wizard_complete);
    if (!hasCompletedWizard) return 'has_project';

    const completedRuns = runs.filter(r => r.status === 'completed');
    if (completedRuns.length === 0) return 'has_first_sync';

    const hasPR = runs.some(r => r.pr_number);
    const manyRuns = completedRuns.length >= 3;
    if (hasPR || manyRuns) return 'graduated';

    return 'has_first_run';
  }, [projects, runs]);

  const dismissWelcome = useCallback(() => {
    setWelcomeDismissed(true);
  }, []);

  const markCelebrationShown = useCallback(() => {
    setCelebrationShown(true);
  }, []);

  const showWelcome = tier === 'new_user' && !welcomeDismissed;

  const value = useMemo<OnboardingState>(() => ({
    tier,
    welcomeDismissed,
    celebrationShown,
    dismissWelcome,
    markCelebrationShown,
    showWelcome,
  }), [tier, welcomeDismissed, celebrationShown, dismissWelcome, markCelebrationShown, showWelcome]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}
